import React, { useState, useEffect, useCallback } from 'react';

/**
 * WorkflowDashboard — counts taken from the database, and nothing else.
 *
 * This page used to call /api/real-time/dashboard-stats, which does not exist,
 * so its figures stayed at zero — and the gaps were filled with invented data:
 * a hardcoded "projects managed: 12", a list of fake recent searches ("dining
 * chairs — 2 min ago") and four made-up top products, under a heading reading
 * "Real-time insights into your interior design workflow efficiency".
 *
 * Everything below comes from /api/analytics/summary. Where a figure cannot be
 * derived from real data it is not shown at all.
 */

const BACKEND = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);

const Stat = ({ label, value, hint }) => (
  <div className="rounded-xl border border-[#B49B7E]/30 bg-black/40 p-5 text-center">
    <div className="text-3xl font-light text-[#D4A574]">{value}</div>
    <div className="text-sm mt-1" style={{ color: '#F5F5DC' }}>{label}</div>
    {hint ? <div className="text-[11px] text-gray-500 mt-1">{hint}</div> : null}
  </div>
);

const WorkflowDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND}/api/analytics/summary`);
      if (!res.ok) throw new Error(`Could not load the figures (${res.status})`);
      setData(await res.json());
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  const exportCsv = () => {
    window.open(`${BACKEND}/api/analytics/summary.csv`, '_blank');
  };

  const t = data?.totals || {};
  const statuses = Object.entries(data?.items_by_status || {});
  const sheets = Object.entries(data?.rooms_by_sheet || {});
  const vendors = data?.top_vendors || [];
  const biggestStatus = statuses.length ? Math.max(...statuses.map(([, n]) => n)) : 0;

  return (
    <div className="w-full max-w-[95%] mx-auto bg-gradient-to-b from-black via-gray-900 to-black p-8 rounded-3xl shadow-2xl border border-[#B49B7E]/20 my-8">
      <div className="text-center mb-10">
        <h2 className="text-3xl font-light text-[#B49B7E] tracking-wide mb-4">
          📊 WORKFLOW ANALYTICS
        </h2>
        <div className="w-32 h-0.5 bg-gradient-to-r from-transparent via-[#B49B7E] to-transparent mx-auto mb-4"></div>
        <p className="text-sm" style={{ color: '#F5F5DC', opacity: 0.8 }}>
          Counted from your projects, rooms and items.
          {data?.generated_at ? ` As of ${new Date(data.generated_at).toLocaleString()}.` : ''}
        </p>
      </div>

      {loading && <p className="text-center text-[#D4C5A9] py-10">Loading…</p>}
      {error && (
        <div className="text-center py-10">
          <p className="text-red-400 mb-3" data-testid="analytics-error">{error}</p>
          <button onClick={load} className="px-4 py-2 bg-[#D4A574] text-black rounded font-bold">
            Try again
          </button>
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-10" data-testid="analytics-totals">
            <Stat label="Projects" value={t.projects ?? 0} />
            <Stat label="Rooms" value={t.rooms ?? 0} />
            <Stat label="Line items" value={t.items ?? 0} />
            <Stat label="Vendors" value={t.vendors ?? 0} hint="distinct names on items" />
            <Stat label="Items with photos" value={t.items_with_photos ?? 0} />
            <Stat label="Finish schedules" value={t.finish_schedules ?? 0} />
            <Stat label="Room attachments" value={t.room_attachments ?? 0} hint="plans & references" />
            <Stat label="Imported materials" value={t.materials_in_library ?? 0} />
            <Stat label="Paint colours" value={t.curated_paint_colours ?? 0} hint="built in" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg text-[#D4A574] font-bold mb-3">Items by status</h3>
              {statuses.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No items yet.</p>
              ) : (
                <ul className="space-y-2" data-testid="analytics-statuses">
                  {statuses.map(([status, n]) => (
                    <li key={status} className="flex items-center gap-3">
                      <span className="text-xs w-48 truncate" style={{ color: '#F5F5DC' }}>{status}</span>
                      <span className="flex-1 h-2 bg-black/60 rounded overflow-hidden">
                        <span className="block h-full bg-[#D4A574]"
                          style={{ width: `${biggestStatus ? (n / biggestStatus) * 100 : 0}%` }} />
                      </span>
                      <span className="text-xs text-[#B49B7E] w-10 text-right">{n}</span>
                    </li>
                  ))}
                </ul>
              )}

              <h3 className="text-lg text-[#D4A574] font-bold mt-6 mb-3">Rooms by sheet</h3>
              <ul className="text-sm space-y-1" style={{ color: '#F5F5DC' }}>
                {sheets.length === 0
                  ? <li className="text-gray-500 italic">No rooms yet.</li>
                  : sheets.map(([sheet, n]) => (
                    <li key={sheet}>{sheet}: <span className="text-[#D4A574] font-bold">{n}</span></li>
                  ))}
              </ul>
            </div>

            <div>
              <h3 className="text-lg text-[#D4A574] font-bold mb-3">Vendors used most</h3>
              {vendors.length === 0 ? (
                <p className="text-sm text-gray-500 italic">
                  No vendors recorded on items yet.
                </p>
              ) : (
                <table className="w-full text-sm" data-testid="analytics-vendors">
                  <thead>
                    <tr className="text-[#B49B7E] text-xs">
                      <th className="text-left pb-2">Vendor</th>
                      <th className="text-right pb-2">Items</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendors.map(v => (
                      <tr key={v.vendor} className="border-t border-[#B49B7E]/15">
                        <td className="py-1.5" style={{ color: '#F5F5DC' }}>{v.vendor}</td>
                        <td className="py-1.5 text-right text-[#D4A574] font-bold">{v.items}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="mt-10 flex justify-center">
            <button
              onClick={exportCsv}
              data-testid="analytics-export"
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 px-6 py-3 rounded-xl font-bold transition-all"
              style={{ color: '#F5F5DC' }}
            >
              📊 Download these figures (CSV)
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default WorkflowDashboard;
